// This utility is adapted from https://github.com/sindresorhus/is-network-error
//
// Main reason for this custom implementation:
// Sentry and similar tools may modify error messages (e.g., by appending the hostname to messages like "Failed to fetch").
// This breaks exact matching with Set.has().
//
// To address this, we check if the error message starts with any known network error prefix (case-insensitive),
// making the detection more robust in environments where error messages are altered.

const objectToString = Object.prototype.toString;

const isError = (value: unknown): value is Error =>
  objectToString.call(value) === "[object Error]";

// List of known network error message prefixes from browsers and libraries.
const errorMessages = [
  "network error", // Chrome
  "Failed to fetch", // Chrome
  "NetworkError when attempting to fetch resource.", // Firefox
  "The Internet connection appears to be offline.", // Safari 16
  "Load failed", // Safari 17+
  "Network request failed", // `cross-fetch`
  "fetch failed", // Undici (Node.js)
  "terminated", // Undici (Node.js)
];

export default function isNetworkError(error: unknown): boolean {
  const isValid =
    error &&
    isError(error) &&
    error.name === "TypeError" &&
    typeof error.message === "string";

  if (!isValid) {
    return false;
  }

  // Special handling for Safari 17+ where the error message is very generic.
  // Network errors in Safari have no stack.
  if (error.message === "Load failed") {
    return error.stack === undefined;
  }

  // Instead of exact match, check if the error message starts with any known prefix.
  // This is necessary because tools like Sentry may append hostnames or other info to the message.
  const message = error.message.toLowerCase();
  return errorMessages.some(msg => message.startsWith(msg.toLowerCase()));
}
